import { Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  BottomSheetLoadingContent,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useSetShiftTimesheetAvailability } from "@src/appV2/redesign/WorkerShifts/UnverifiedShifts/api/useSetShiftTimesheetAvailability";
import { useSupportLinks } from "@src/appV2/support/hooks/useSupportLinks";
import { useZendeskArticle } from "@src/appV2/Zendesk/useZendeskArticle";
import { useState } from "react";

export interface NoTimesheetBottomSheetProps {
  modalState: UseModalState;
  shiftId: string;
  agentId: string;
  agentName: string;
  onContinue: () => void;
}

type ContentType = "choose-option" | "forgot-photo" | "sent-another-way";

export function NoTimesheetBottomSheet(props: NoTimesheetBottomSheetProps) {
  const { modalState, shiftId, agentId, agentName, onContinue } = props;

  const [contentType, setContentType] = useState<ContentType>("choose-option");

  const { openArticle } = useZendeskArticle();
  const { timesheetSignaturesSupportLink } = useSupportLinks();

  const {
    mutateAsync: setShiftTimesheetAvailability,
    isLoading: isLoadingSetTimesheetAvailability,
  } = useSetShiftTimesheetAvailability({
    meta: {
      userErrorMessage: "Something went wrong while updating timesheet",
    },
  });
  const setTimesheetUnavailable = async (reason: string) => {
    await setShiftTimesheetAvailability({
      shiftId,
      reason,
      agentId,
      agentName,
    });
  };

  return (
    <BottomSheet
      modalState={modalState}
      canDismiss={!isLoadingSetTimesheetAvailability && contentType === "choose-option"}
    >
      {isLoadingSetTimesheetAvailability && (
        <BottomSheetLoadingContent
          title="Submitting"
          description="Should only take a few seconds"
        />
      )}

      {!isLoadingSetTimesheetAvailability && contentType === "choose-option" && (
        <Stack direction="column" spacing={4} padding={4} paddingBottom={6} justifyContent="center">
          <Button
            size="large"
            variant="outlined"
            onClick={async () => {
              await setTimesheetUnavailable(APP_V2_APP_EVENTS.CANCEL_TIMESHEET_PHOTO_FORGOTTEN);
              await openArticle(timesheetSignaturesSupportLink);
              logEvent(APP_V2_APP_EVENTS.CANCEL_TIMESHEET_PHOTO_FORGOTTEN);
              setContentType("forgot-photo");
            }}
          >
            I forgot to take a photo
          </Button>
          <Button
            size="large"
            variant="outlined"
            onClick={async () => {
              await setTimesheetUnavailable(APP_V2_APP_EVENTS.CANCEL_TIMECARD_SENT_ANOTHER_WAY);
              logEvent(APP_V2_APP_EVENTS.CANCEL_TIMECARD_SENT_ANOTHER_WAY);
              setContentType("sent-another-way");
            }}
          >
            I sent it to Clipboard another way
          </Button>

          <IconButton
            aria-label="Cancel timesheet upload"
            size="large"
            variant="outlined"
            iconType="close"
            sx={{ alignSelf: "center" }}
            onClick={() => {
              modalState.closeModal();
            }}
          />
        </Stack>
      )}

      {!isLoadingSetTimesheetAvailability && contentType === "forgot-photo" && (
        <>
          <BottomSheetIllustrationContent
            illustrationType="warning"
            title="Thanks for letting us know!"
            description="In order to get paid quickly, we require an uploaded timesheet. Without a timesheet, it can take up to a week to verify your shift."
          />
          <DialogFooter orientation="vertical">
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                onContinue();
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </>
      )}

      {!isLoadingSetTimesheetAvailability && contentType === "sent-another-way" && (
        <>
          <BottomSheetIllustrationContent
            illustrationType="document-review"
            title="Thank you!"
            description="We'll take a look. Uploading your timesheet in the app is the quickest way to get paid."
          />
          <DialogFooter orientation="vertical">
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                onContinue();
              }}
            >
              Continue
            </Button>
          </DialogFooter>
        </>
      )}
    </BottomSheet>
  );
}
